// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo, useState } from 'react';

import { useEvolvePaginatedGet, type WorkOrderId, type WorkOrderItem, type WorkOrderItemId } from '@evolvemep/api-ts';
import { Box, Checkbox, Flex, ScrollArea, Stack, Text } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { TextInputDebounced } from 'components/Mantine/TextInputDebounced';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import type { ShipmentItemsFormValues } from 'modules/Shop/Shipments/ShipmentDetail/AddShipmentItemsModal/AddShipmentItemsForm';
import { ShipmentWorkOrderItemRow } from 'modules/Shop/Shipments/ShipmentDetail/AddShipmentItemsModal/ShipmentWorkOrderItemRow';
import { getRemainingItemsToShip } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';

type Props = {
  form: UseFormReturnType<ShipmentItemsFormValues>;
  workOrderId: WorkOrderId;
};

export const ShipmentItemsSelector = ({ form, workOrderId }: Props) => {
  const [search, setSearch] = useState('');
  const allSelected = form.values.allSelectedWorkOrderIds.includes(workOrderId);

  const defaultOpts = useMemo(
    () => ({
      defaultConfig: {
        params: { workOrderId, name: search || undefined },
      },
    }),
    [workOrderId, search],
  );
  const {
    data: items,
    entireCount,
    setDefaultOpts,
    fetchNextPage,
    loading,
  } = useEvolvePaginatedGet('shop/workOrder/item', defaultOpts);
  useEffect(() => setDefaultOpts(defaultOpts), [defaultOpts, setDefaultOpts]);

  const itemIndexWorkOrderIdLookup = useMemo(
    () => new Map(form.values.items.map(({ workOrderItemId }, i) => [workOrderItemId, i])),
    [form.values.items],
  );

  const allItemsAllocated = useMemo(
    () => items.length === entireCount && items.every((woi) => getRemainingItemsToShip(woi) === 0),
    [entireCount, items],
  );

  const handleAdd = (woi: WorkOrderItem) => {
    form.insertListItem('items', {
      workOrderItemId: woi.workOrderItemId,
      name: woi.name,
      quantity: getRemainingItemsToShip(woi),
      workOrderId: woi.workOrderId,
    });
  };

  const handleToggle = (item: WorkOrderItem) => {
    if (allSelected) return;
    const { workOrderItemId } = item;
    const index = itemIndexWorkOrderIdLookup.get(workOrderItemId);
    if (isNotNil(index)) {
      form.removeListItem('items', index);
    } else {
      handleAdd(item);
    }
  };

  const handleToggleSelectAll = () => {
    if (allSelected) {
      form.setFieldValue(
        'allSelectedWorkOrderIds',
        form.values.allSelectedWorkOrderIds.filter((id) => id !== workOrderId),
      );
    } else {
      form.setFieldValue('allSelectedWorkOrderIds', [...form.values.allSelectedWorkOrderIds, workOrderId]);
      form.setFieldValue(
        'items',
        form.values.items.filter((item) => item.workOrderId !== workOrderId),
      );
    }
  };

  const handleChangeQuantity = (workOrderItemId: WorkOrderItemId, quantity: number) => {
    if (allSelected) return;
    const index = itemIndexWorkOrderIdLookup.get(workOrderItemId);
    if (isNil(index)) return;
    const item = form.values.items[index];
    if (isNil(item)) return;
    form.replaceListItem('items', index, { ...item, quantity });
  };

  const getQuantity = (workOrderItem: WorkOrderItem) => {
    const remainingToShip = getRemainingItemsToShip(workOrderItem);
    if (allSelected && remainingToShip > 0) return remainingToShip;
    const index = itemIndexWorkOrderIdLookup.get(workOrderItem.workOrderItemId);
    if (isNotNil(index) && isNotNil(form.values.items[index])) {
      return form.values.items[index].quantity;
    }
    return undefined;
  };

  return (
    <Stack flex={1} gap="xs" mih={0}>
      <Flex align="center" gap="sm">
        <Checkbox
          label="Select all"
          size="sm"
          checked={allSelected}
          disabled={(!allSelected && (search.length > 0 || allItemsAllocated)) || loading}
          onChange={handleToggleSelectAll}
        />
        <TextInputDebounced
          flex={1}
          size="sm"
          placeholder="Search items"
          leftSection={<EvolveIcon icon="Search" size="sm" />}
          value={search}
          onChange={setSearch}
          disabled={allSelected}
        />
      </Flex>
      <ScrollArea flex={1}>
        <Stack gap="xs">
          {items.map((item) => (
            <ShipmentWorkOrderItemRow
              key={item.workOrderItemId}
              item={item}
              onToggle={() => handleToggle(item)}
              onQuantityChange={(qty) => handleChangeQuantity(item.workOrderItemId, qty)}
              selectedQuantity={getQuantity(item)}
              disabled={allSelected}
            />
          ))}
          <TriggerOnVisible onVisible={fetchNextPage} loading={loading} />
          {!loading && items.length === 0 && (
            <Box p="md">
              <Text c="dimmed" fz="sm">
                {search ? 'No items match your search.' : 'No items on this work order.'}
              </Text>
            </Box>
          )}
        </Stack>
      </ScrollArea>
    </Stack>
  );
};
